import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { INLINE_CTA } from "../../lib/blog-markers";

export const listeDeNaissanceQuandPartager: BlogPost = {
  h1: "Liste de naissance : quand la faire, comment la partager, et les règles de politesse",
  seoTitle: "Liste de naissance : quand la faire et comment l'annoncer sans gêne · Listou",
  seoDescription:
    "À quel mois créer sa liste de naissance, comment l'annoncer sans paraître intéressé, la partager sur le faire-part, gérer les cadeaux hors liste et remercier. Les usages, étape par étape.",
  publishedOn: "2026-10-20",
  updatedOn: "2026-10-20",
  summary:
    "On crée sa liste de naissance vers le 5e ou 6e mois de grossesse et on la partage à partir du 7e, pour que les proches aient le temps d'offrir avant la naissance. On ne l'envoie pas d'office : on la donne à ceux qui demandent, puis on l'ajoute au faire-part avec un QR code. Les cadeaux hors liste sont les bienvenus, et chaque cadeau appelle un remerciement, dans le mois si possible.",
  body: `
## Quand créer sa liste de naissance ?

Vers le **5e ou 6e mois** de grossesse. Avant, beaucoup de parents préfèrent attendre la deuxième échographie et connaître, s'ils le souhaitent, le sexe du bébé. Après, il reste peu de temps pour comparer les poussettes et les sièges auto.

Rien n'oblige à tout remplir d'un coup. Commencez par l'essentiel du premier mois (le couchage, le siège auto, la toilette) et ajoutez le reste au fil des semaines. La liste se met à jour pour tout le monde, même après l'avoir partagée.

Vous ne connaissez pas encore le prénom ? Ce n'est pas un problème : sur Listou, le titre et la page se modifient à tout moment, et le lien reste le même.

## Quand la partager ?

À partir du **7e mois**, à ceux qui demandent. Les grands-parents et les proches posent souvent la question bien avant : c'est le bon moment pour leur envoyer le lien.

Ensuite, deux temps :

- **Avant la naissance**, pour le matériel du premier mois. Un siège auto offert à la maternité arrive trop tard.
- **Après la naissance**, avec le faire-part, pour les vêtements des tailles suivantes, les jouets et tout ce qui servira plus tard.

Une liste partagée tôt n'est pas une liste close : continuez d'y ajouter des envies pour la deuxième vague.

## Comment l'annoncer sans gêne

La crainte de passer pour intéressé retient beaucoup de parents. Elle est rarement justifiée : la plupart des proches comptent offrir un cadeau, et une liste leur évite de deviner et de doublonner.

Quelques usages simples :

- **Ne l'envoyez pas d'office** à toute la famille dans un message groupé. Répondez à ceux qui demandent « qu'est-ce qui vous ferait plaisir ? ».
- **Présentez-la comme une aide** : « On a fait une liste pour éviter les doublons, si ça peut t'aider. »
- **Ne mettez pas de montant attendu**, ni de rappel.
- **Gardez des petits prix**, pour que chacun puisse offrir sans se forcer. Notre article [que mettre sur une liste de naissance](/blog/que-mettre-liste-de-naissance) détaille comment étaler les budgets.

## Sur le faire-part

Le faire-part est l'endroit où la liste circule le plus, et le plus naturellement : une ligne discrète en bas suffit, sans insister. Une adresse longue ne se recopie pas ; un **QR code** se scanne en une seconde.

Sur [Listou](/liste-de-naissance), le QR code de la liste se télécharge pour l'imprimer sur le faire-part, et la page se partage aussi par lien, par message ou en visuel pour Instagram. La page s'habille aux couleurs du faire-part : modèle de page, palette, police et stickers se choisissent avec l'aperçu en direct.

Une formule sobre fonctionne toujours : « Pour ceux qui le souhaitent, notre liste de naissance » suivi du QR code.
${INLINE_CTA}## Garder la surprise

Une liste de naissance n'est pas un bon de commande. Beaucoup de parents aiment découvrir les cadeaux à l'ouverture, pas dans un tableau de suivi.

Sur Listou, le mode surprise est activé dès la création : vous recevez seulement « un cadeau vient d'être réservé », sans savoir lequel ni par qui, jusqu'au lendemain de la date de l'événement. Le co-parent, co-propriétaire de la liste, ne voit rien non plus. Un proche peut cocher « Rester anonyme » : son nom reste caché même après.

## Les cadeaux hors liste

Ils arriveront, et c'est très bien. Une grand-tante tricote une brassière, un ami offre le livre qui a marqué son enfance. Une liste guide les proches qui veulent être guidés ; elle n'interdit rien aux autres.

Deux cas méritent une petite action :

- **Un cadeau hors liste qui fait doublon** avec une envie : retirez l'envie de la liste, pour que personne ne l'offre une seconde fois.
- **Un proche qui demande s'il peut offrir autre chose** : répondez oui, sans réserve.

Et si vous recevez deux fois le même article, échangez-le discrètement. Personne n'a besoin de le savoir.

## Remercier

Chaque cadeau appelle un merci, de préférence **dans le mois** qui suit la naissance ou la réception. Les premières semaines sont difficiles : personne ne vous en voudra d'un peu de retard, mais personne ne doit être oublié.

- **Un message personnel** suffit pour les proches : nommez le cadeau et dites à quoi il sert déjà. « La gigoteuse est parfaite, il dort dedans depuis la maternité. »
- **Une carte**, avec une photo du bébé, pour les grands-parents et ceux qui aiment les garder.
- **Une photo du cadeau utilisé** fait toujours plaisir à celui qui l'a choisi.

Avec le mode surprise, vous découvrez qui a offert quoi le lendemain de la date de l'événement : c'est le moment de dresser la liste des remerciements.

## Pour un deuxième enfant

Une liste pour le deuxième est tout à fait acceptée, à condition qu'elle ressemble à un deuxième enfant : moins de matériel, puisque vous l'avez déjà, et plus de ce qui s'use ou ne se transmet pas. Une poussette double, un deuxième siège auto, des vêtements de la saison du nouveau-né, et des cadeaux pour l'aîné, qui accueillera mieux le bébé s'il n'est pas oublié.
`,
  faq: [
    [
      "À quel mois faire sa liste de naissance ?",
      "Vers le 5e ou 6e mois de grossesse, et la partager à partir du 7e mois. Cela laisse aux proches le temps d'offrir le matériel du premier mois avant la naissance.",
    ],
    [
      "Est-ce impoli de faire une liste de naissance ?",
      "Non. La plupart des proches comptent offrir un cadeau, et une liste leur évite de deviner et de doublonner. Il suffit de la donner à ceux qui demandent plutôt que de l'envoyer à tout le monde.",
    ],
    [
      "Comment mettre sa liste de naissance sur le faire-part ?",
      "Avec un QR code, qui se scanne en une seconde, et une ligne sobre comme « Pour ceux qui le souhaitent, notre liste de naissance ». Listou génère le QR code de la liste, prêt à imprimer.",
    ],
    [
      "Peut-on faire une liste de naissance pour un deuxième enfant ?",
      "Oui. Elle sera plus courte : moins de matériel, déjà acheté pour l'aîné, et plus de vêtements de saison, de consommables et de cadeaux pour le grand frère ou la grande sœur.",
    ],
    [
      "Que faire si on reçoit un cadeau en double ?",
      "Échangez-le discrètement. Et si un cadeau hors liste correspond à une envie, retirez cette envie de la liste pour éviter qu'elle soit offerte une seconde fois.",
    ],
    [
      "Les parents peuvent-ils ne pas voir les cadeaux réservés ?",
      "Sur Listou, oui, par défaut : le mode surprise cache les réservations aux deux parents jusqu'au lendemain de la date de l'événement. Un proche peut aussi rester anonyme, même après.",
    ],
  ],
  occasion: "birth",
  inlineCta: {
    title: "Le QR code de votre liste, prêt pour le faire-part",
    text: "Créez votre liste, partagez-la par lien, message ou QR code.",
    sticker: stickerUrl("bebe", "cigogne"),
  },
};
